import type { HtmlParserPlugin } from '~/plugins/dom-parser'
import type { BookAndArticle } from '~/utils/solr/literature'
import { LAW_BASE_PATH } from '#shared/constants'
import { ELEMENT_NODE, TEXT_NODE } from '#shared/utils/dom'
import { PAGE_ID_QUERY } from '~/constants/urls'

/**
 * Creates a plugin that removes the book title and subtitle from the book_title page DOM
 * if they are already present in the book data
 *
 * @param book The book data to compare against
 */
export function createBookTitlePlugin(book: BookAndArticle | null | undefined): HtmlParserPlugin {
  const title = book?.title?.toLowerCase() as Lowercase<string> | undefined
  const subtitle = book?.subtitle?.toLowerCase() as Lowercase<string> | undefined

  return (page: Document) => {
    if (!title) {
      return page
    }

    const h1 = page?.querySelector('h1')
    if (!h1) {
      return page
    }

    // The book_title page DOM has a h1 with a title attribute, or only one span
    if (
      h1.getAttribute('epub:type') === 'fulltitle'
      || h1.classList.contains('title')
      || h1.querySelectorAll('span').length === 1
    ) {
      const text = h1.textContent?.trim()?.toLowerCase()

      if (text === title || (text?.includes(title) && subtitle && text?.includes(subtitle))) {
        h1.remove()
      }
    }
    // Check if the book_title DOM contains a h1 with title or subtitle spans
    else {
      const spans = Array.from(h1.querySelectorAll('span'))

      function findMatch(type: 'title' | 'subtitle', defaultIndex: 0 | 1) {
        const match = spans.find((s: Element) => s.getAttribute('epub:type') === type || s.classList.contains(type)) || spans[defaultIndex]
        const content = match ? (match as Element).textContent?.trim() : undefined

        return {
          content,
          matches(value: Lowercase<string> | undefined) {
            return content?.toLowerCase() === value
          },
        }
      }

      const titleSpan = findMatch('title', 0)
      if (!titleSpan.matches(title)) {
        // We have a title in the DOM that does not match the book data -> Keep the whole h1
        return page
      }

      const subtitleSpan = findMatch('subtitle', 1)
      if (subtitleSpan.content && !subtitleSpan.matches(subtitle)) {
        // We have a subtitle in the DOM that does not match the book data -> Keep it, but in an h2
        const h2 = page.createElement('h2')
        h2.textContent = subtitleSpan.content
        h1.replaceWith(h2)
      }
      else {
        // Either the subtitle does not exist in the DOM, or it matches the book data -> Remove the h1
        h1.remove()
      }
    }

    return page
  }
}

const EXTERNAL_WEB_LINK = /^https?:\/\//i
const MAILTO_LINK = /^mailto:/i

/**
 * Creates a plugin that processes all links in the document. Footnote links (noteref) are preserved,
 * while other links are either converted to page navigation or removed based on the content source.
 * For EPUB content:
 * - Links matching pattern `*.xhtml#p(number)` are converted to `?pageId=(number)`
 * - Links matching pattern `*.xhtml#ind*` are converted to `?pageId=(link text content)`
 * - `http:` and `https:` links are kept and open in a new tab; `mailto:` links are kept as they are
 * - Other links are removed but their text content is preserved
 *
 * In all other content, all links except footnote references are removed, preserving the text content
 *
 * @param doc The current document to process links on
 * @param ctx The context passed in from where the plugin is called, here used to check content source
 */
export const processLinks: HtmlParserPlugin = (doc, ctx) => {
  const links = Array.from(doc.body.querySelectorAll('a'))
    .filter(link => link.getAttribute('epub:type') !== 'noteref')

  for (const link of links) {
    const parent = link.parentNode
    if (!parent) {
      continue
    }

    const href = link.getAttribute('href')

    if (ctx?.source === 'epub') {
      let pageId: string | undefined

      const pMatch = href?.match(/^.*\.xhtml#p(\d+)$/)
      if (pMatch) {
        pageId = pMatch[1]
      }
      else if (href?.match(/^.*\.xhtml#ind/)) {
        const textContent = link.textContent?.trim()

        // The text content should be a number
        if (textContent && /^\d+$/.test(textContent)) {
          pageId = textContent
        }
      }

      if (pageId) {
        link.setAttribute('href', `?${PAGE_ID_QUERY}=${pageId}`)
        continue
      }

      if (href && EXTERNAL_WEB_LINK.test(href)) {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
        continue
      }

      if (href && MAILTO_LINK.test(href)) {
        continue
      }
    }

    const fragment = doc.createDocumentFragment()

    while (link.firstChild) {
      fragment.appendChild(link.firstChild)
    }

    parent.insertBefore(fragment, link)
    link.remove()
  }

  return doc
}

/**
 * Removes EPUB-internal resource references that the browser would otherwise
 * resolve against the current page path (`/faglitteratur/bok/`) and 404 on.
 *
 * The book bodytext stored in Solr is raw EPUB HTML. It carries relative
 * resource references that mean nothing on juridika.no:
 *   - `<link rel="stylesheet" href="CSS/stylesheet.css">` and `<link rel="next"
 *     href="chapter5.xhtml">` (EPUB packaging/navigation links)
 *   - `<style>` blocks with relative `@import`s
 *   - relative `<img>`/`<source>` `src`s (e.g. `images/figure1.png`)
 * Inserted via `v-html`, these trigger real network requests like
 * `/faglitteratur/bok/CSS/stylesheet.css` → 404. `processLinks` only handles
 * `<a>` tags, so these slip through. (Measured: 571 .css + 59 .xhtml 404-er / 14d.)
 *
 * We strip them: BodyText.vue owns all rendering styling, and EPUB navigation/
 * packaging links are meaningless in the SPA. Anchor links are left to
 * `processLinks`. Absolute and protocol/`data:` resources are preserved — only
 * EPUB-relative references (no scheme, not root-absolute) are removed, so a
 * legitimately absolute asset URL is never touched.
 */
export const stripEpubResources: HtmlParserPlugin = (doc) => {
  // <link> and <style> are never valid on the rendered page — remove outright.
  for (const el of doc.body.querySelectorAll('link, style')) {
    el.remove()
  }

  // A relative resource URL: no scheme (http:, https:, data:, //), not
  // root-absolute (/...), not an in-page fragment (#...). These are exactly the
  // EPUB-relative refs that resolve against the book path and 404.
  const isEpubRelative = (url: string | null): boolean => {
    if (!url) {
      return false
    }
    const v = url.trim()
    if (v === '') {
      return false
    }
    // Non-capturing group: a scheme (`https:`), protocol-relative (`//`),
    // root-absolute (`/`), or in-page fragment (`#`) is NOT EPUB-relative.
    return !/^(?:[a-z][a-z0-9+.-]*:|\/\/|\/|#)/i.test(v)
  }

  // A `srcset` is a comma-separated list of `<url> [descriptor]` candidates
  // (e.g. `images/hi.png 2x, images/lo.png 1x`). Any EPUB-relative candidate
  // would 404, so treat the element as relative if ANY candidate is relative.
  const srcsetHasRelative = (srcset: string | null): boolean => {
    if (!srcset) {
      return false
    }
    return srcset
      .split(',')
      .map(candidate => candidate.trim().split(/\s+/)[0] ?? '')
      .some(isEpubRelative)
  }

  // Relative <img>/<source> resources → drop the element (it would 404). Covers
  // both `src` and the responsive `srcset` (the normal `<picture>` form). Keep
  // any element whose references are all absolute/proxied/data:. (Codex/Gemini-review.)
  for (const el of doc.body.querySelectorAll('img[src], img[srcset], source[src], source[srcset]')) {
    if (isEpubRelative(el.getAttribute('src')) || srcsetHasRelative(el.getAttribute('srcset'))) {
      el.remove()
    }
  }

  return doc
}

/**
 * Fixes broken attribute values wrapped in escaped quotes,
 * for example `src=\"...\"` instead of `src="..."`.
 *
 * This happens in some Ibexa bodytext content and can break
 * links, styles, images, and footnotes.
 */
export const cleanCorruptedAttributes: HtmlParserPlugin = (doc) => {
  const wrapped = /^\\"(.+)\\"$/
  for (const el of doc.body.querySelectorAll('*')) {
    for (const attr of Array.from(el.attributes)) {
      const match = attr.value.match(wrapped)
      if (match?.[1]) {
        el.setAttribute(attr.name, match[1])
      }
    }
  }
  return doc
}

/**
 * Convert raw content IDs to proper URL paths
 */
function resolveContentId(href: string): string | null {
  if (href.startsWith(`/${LAW_BASE_PATH}/`) || href.startsWith('/faglitteratur/')) {
    return null
  }

  const normalizedHref = href.startsWith('/') ? href.slice(1) : href

  if (/^(?:lov|forskrift)-\d{4}-\d{2}-\d{2}-\d+/.test(normalizedHref)) {
    return `/${LAW_BASE_PATH}/${normalizedHref}`
  }

  if (normalizedHref.startsWith('isbn-')) {
    return `/faglitteratur/bok/${normalizedHref}`
  }

  if (/^[a-f0-9]{32}$/.test(normalizedHref)) {
    return `/faglitteratur/artikkel/${normalizedHref}`
  }

  return null
}

/**
 * Plugin that processes links for news content.
 * - Raw content IDs are converted to proper URL paths
 * - All links open in a new tab (target="_blank")
 */
export const processNewsLinks: HtmlParserPlugin = (doc) => {
  const links = doc.body.querySelectorAll('a')

  for (const link of links) {
    const href = link.getAttribute('href')
    if (!href) {
      continue
    }

    const resolvedPath = resolveContentId(href)
    if (resolvedPath) {
      link.setAttribute('href', resolvedPath)
    }

    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  }

  return doc
}

/**
 * Truncates HTML content while preserving tag structure.
 *
 * @param element - The DOM element to truncate
 * @param maxChars - Maximum character count (text content only)
 * @param ellipsis - Optional string to append when truncated (default: none)
 */
export function truncateHtmlContent(element: Element, maxChars: number, ellipsis: string = ''): string {
  const clone = element.cloneNode(true) as Element
  let remaining = maxChars

  function processNode(node: Node): boolean {
    if (remaining <= 0) {
      return false
    }

    if (node.nodeType === TEXT_NODE) {
      const text = node.textContent || ''
      if (text.length <= remaining) {
        remaining -= text.length
        return true
      }
      node.textContent = text.slice(0, remaining) + ellipsis
      remaining = 0
      return false
    }

    if (node.nodeType === ELEMENT_NODE) {
      const children = Array.from(node.childNodes)
      for (const child of children) {
        if (!processNode(child)) {
          let sibling = child.nextSibling
          while (sibling) {
            const next = sibling.nextSibling
            node.removeChild(sibling)
            sibling = next
          }
          return false
        }
      }
    }

    return true
  }

  processNode(clone)
  return clone.innerHTML
}

export const anchorTargetBlank: HtmlParserPlugin = (doc, ctx) => {
  const links = doc.querySelectorAll('a')
  links.forEach((link) => {
    const href = link.getAttribute('href')

    if (!href || href.startsWith('#')) {
      return
    }

    if (ctx?.siteUrl && !href.startsWith(ctx.siteUrl)) {
      link.setAttribute('target', '_blank')
      link.setAttribute('rel', 'noopener noreferrer')
    }
  })

  return doc
}

/**
 * Extracts paragraph text fragments from raw HTML.
 *
 * - If the HTML contains <p>...</p> blocks, returns their inner text as an array.
 * - Otherwise falls back to splitting on double newlines or <br> tags and strips remaining HTML tags.
 *
 * @param html Raw HTML string
 * @returns Array of paragraph strings (without surrounding tags)
 */
export function extractParagraphFragments(html = ''): string[] {
  const pMatches = html.match(/<p\b[^>]*>(.*?)<\/p>/gis)
  if (pMatches && pMatches.length) {
    return pMatches.map(m => m.replace(/<p\b[^>]*>|<\/p>/gi, '').trim()).filter(Boolean)
  }
  const parts = html
    .replace(/<br\s*\/?>/gi, '\n')
    .split(/\n\s*\n/)
    .map(s => s.replace(/<[^>]+>/g, '').trim())
    .filter(Boolean)
  return parts
}

/**
 * Builds an HTML preview composed of the first `count` paragraphs while preserving tag structure.
 *
 * - If the source contains <p>...</p> elements, returns the first `count` of those elements joined.
 * - Otherwise uses extractParagraphFragments to obtain plain text fragments and wraps them in <p> tags.
 *
 * @param html Raw HTML string
 * @param count Number of paragraphs to include (default: 2)
 * @returns HTML string containing up to `count` paragraph elements
 */
export function buildPreviewHtml(html = '', count = 2): string {
  const pMatches = html.match(/<p\b[^>]*>.*?<\/p>/gis)
  if (pMatches && pMatches.length) {
    return pMatches.slice(0, count).join('')
  }
  const frags = extractParagraphFragments(html)
  return frags.slice(0, count).map(p => `<p>${p}</p>`).join('')
}

/**
 * Unwraps margin text elements that incorrectly wrap multiple children.
 * @param html - The HTML content to process
 * @returns The corrected HTML with properly unwrapped margin text
 */
export function unwrapMarginText(html: string): string {
  // Should only run if we detect the margtekst-class
  if (!html?.includes('margtekst')) {
    return html
  }

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const firstElement = doc.body.firstElementChild

    // Only process if the root element is .margtekst
    // This issue only occurs when margtekst wraps the entire content
    if (!firstElement?.classList.contains('margtekst')) {
      return html
    }

    const children = Array.from(firstElement.children)

    // Only unwrap if there are multiple children
    if (children.length > 1) {
      // Our somewhat shaky assumption is that only the first child is a paragraph belonging to the margin text,
      // while the rest are main content that should be siblings to the margin element.
      // This assumption does hold true for the only known case (Tvisteloven), but is probably not reliable in general.
      const siblingsToMove = children.slice(1)

      for (let i = siblingsToMove.length - 1; i >= 0; i--) {
        const sibling = siblingsToMove[i]
        if (firstElement.parentNode && sibling) {
          firstElement.parentNode.insertBefore(sibling, firstElement.nextSibling)
        }
      }

      // Remove whitespace-only text nodes from the margin element
      const childNodes = Array.from(firstElement.childNodes)
      for (const node of childNodes) {
        if (node.nodeType === 3 && node.textContent?.trim() === '') {
          firstElement.removeChild(node)
        }
      }
    }

    return doc.body.innerHTML
  }
  catch {
    return html
  }
}
